import { Typography, Divider, Box, Button, TextField, Snackbar, Modal } from "@mui/material";
import WarningIcon from '@mui/icons-material/Warning';
import MuiAlert from '@mui/material/Alert';
import React, { useRef, useState, KeyboardEvent, useContext } from "react";
import { ContentWrapper, SectionWrapper, ModalStyle } from './styles';
import NucleusKit from "../../../Models/NucleusKit";
import NucleusGrc from "../../../Models/NucleusGrc";
import { GetNucleusKit, UpsertCompWellnessPatient, GetCompWellnessPatients, CompWellnessSpecimenReceived, GetNucleusGrc } from "../../../Graphql/Api";
import CompWellnessPatient from "../../../Models/CompWellnessPatient";
import { UpdatePatientFirstName, UpdatePatientLastName, UpdatePatientDateOfBirth, UpdatePatientGender } from './utils';
import { AuthContext } from '../../../Context/Auth/AuthContext';
import "react-datepicker/dist/react-datepicker.css";
import GraphqlExceptionLogging from "../../../Utils/GraphqlExceptionLogging";

// In order of preference, as a kit can carry both the V2 lab line and its data line.
const AGENA_PANEL_PRODUCT_CODES = ["au_nt_custom_agena_panel", "nt_custom_agena_panel_v2", "nt_custom_agena_panel_v2_data"];

const findAgenaPanelStatus = (nucleusKit: NucleusKit | null) =>
    AGENA_PANEL_PRODUCT_CODES
        .map(productCode => nucleusKit?.currentStatuses.find(status => status.productCode === productCode))
        .find(status => status !== undefined);

// A GRC number, with the kit number it was looked up for.
type KitGrc = {
    kitNumber: string,
    grcNumber: string
};

const isSameKit = (kitNumber: string | undefined, otherKitNumber: string | undefined) =>
    !!kitNumber && kitNumber.toUpperCase() === otherKitNumber?.toUpperCase();

// The LIMS API puts its message for a rejected request in errorInfo, as JSON.
const getApiErrorMessage = (error: any): string => {
    try {
        const errorInfo = JSON.parse(error?.errors?.[0]?.errorInfo);
        return errorInfo?.message ?? errorInfo?.Message ?? "";
    }
    catch {
        return "";
    }
};

const ComprehensiveWellnessSpecimenReceptionPage = () => {
    const [currentNucleusKit, setCurrentNucleusKit] = useState<NucleusKit | null>(null);
    const [currentNucleusGrc, setCurrentNucleusGrc] = useState<KitGrc | null>(null);

    const [toastOpen, setToastOpen] = useState<boolean>(false);
    const [toastError, setToastError] = useState<string>("");

    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [modalTextElement, setModalTextElement] = useState<JSX.Element>(<></>);

    const authContext = useContext(AuthContext);
    const kitNumberRef = useRef<HTMLInputElement | null>(null);
    const latestKitNumberRef = useRef<string | null>(null);

    const isReadyToSave = isSameKit(currentNucleusGrc?.kitNumber, currentNucleusKit?.kitNumber);

    const handleScanBarcode = (event: KeyboardEvent) => {
        const kitNumber = kitNumberRef.current?.value.trim().toUpperCase();
        let regex = new RegExp("^((PN|MD|US|VE)[ABCDEFGHJKLMNPQRTUVWXYZ2346789]{6}|VIP[ABCDEFGHJKLMNPQRTUVWXYZ2346789]{5})$");

        if (event.key !== "Enter" || !kitNumber) {
            return;
        }

        // Selecting the scanned text makes the next scan replace it rather than run on from it.
        kitNumberRef.current?.select();

        if (!regex.test(kitNumber)) {
            alert("\"" + kitNumber + "\" is not a valid kit number. Scan the kit again.");
            return;
        }

        // Each scan starts empty and ignores answers for earlier scans, so a lookup that fails or
        // answers late cannot leave one kit's details next to another kit's GRC number.
        latestKitNumberRef.current = kitNumber;
        setCurrentNucleusKit(null);
        setCurrentNucleusGrc(null);
        const isLatestScan = () => latestKitNumberRef.current === kitNumber;

        GetNucleusKit(kitNumber)
        .then((data: NucleusKit) => {
            if (!isLatestScan()) {
                return;
            }
            if (!data || !isSameKit(data.kitNumber, kitNumber)) {
                alert("Could not find kit " + kitNumber + " in Nucleus.")
                return
            }
            setCurrentNucleusKit(data);
        })
        .catch(error => {
            if (!isLatestScan()) {
                return;
            }
            var ErrorMessage = "Error when getting the Nucleus patient using the kit barcode.";
            if (error.errors?.length > 0) {
                console.error(error.errors[0].message);
                alert(error.errors[0].message);
            } 
            else {
                console.error(ErrorMessage);
                alert(ErrorMessage);
            }
        });

        GetNucleusGrc(kitNumber)
        .then((data: NucleusGrc) => {
            if (!isLatestScan()) {
                return;
            }
            if (!data?.grcNumber) {
                alert("Could not find a GRC number for kit " + kitNumber + " in Nucleus.")
                return
            }
            setCurrentNucleusGrc({ kitNumber: kitNumber, grcNumber: data.grcNumber });
        })
        .catch(error => {
            if (!isLatestScan()) {
                return;
            }
            var ErrorMessage = "Error when getting the Nucleus GRC number using the kit barcode.";
            if (error.errors?.length > 0) {
                console.error(error.errors[0].message);
                alert(error.errors[0].message);
            } 
            else {
                console.error(ErrorMessage);
                alert(ErrorMessage);
            }
        });
    }

    const handlePatientFirstNameChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        var current = currentNucleusKit ?? null 
        setCurrentNucleusKit(UpdatePatientFirstName(current, event.target.value));
    };

    const handlePatientLastNameChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        var current = currentNucleusKit ?? null 
        setCurrentNucleusKit(UpdatePatientLastName(current, event.target.value));
    };

    const handlePatientDateOfBirthChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        var current = currentNucleusKit ?? null 
        setCurrentNucleusKit(UpdatePatientDateOfBirth(current, event.target.value));
    };

    const handlePatientGenderChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        var current = currentNucleusKit ?? null 
        setCurrentNucleusKit(UpdatePatientGender(current, event.target.value));
    };

    const handleToastClose = () => {
        setToastOpen(false);
        setToastError("");
    }
    
    const handleSavePatientDetails = () => {
        if (!isReadyToSave) {
            alert("The kit details and GRC number on screen are not for the same kit. Scan the kit again.");
            return;
        }

        var agenaPanelStatus = findAgenaPanelStatus(currentNucleusKit);

        if (!agenaPanelStatus?.productName) {
            alert("A product name is required.");
            return;
        }

        var dateOfBirth = currentNucleusKit?.attributes.find(attribute => attribute.name === "dateOfBirth")?.value ?? "";

        if (!dateOfBirth) {
            alert("A date of birth is required.");
            return;
        }

        var patient: CompWellnessPatient = 
            {
                id: currentNucleusKit?.currentStatuses?.find(status => status.orderId)?.orderId ?? "",
                grcNumber: currentNucleusGrc?.grcNumber ?? "",
                kitNumber: currentNucleusKit?.kitNumber ?? "",
                sex: currentNucleusKit?.gender ?? "",
                firstName: currentNucleusKit?.attributes.find(attribute => attribute.name === "firstName")?.value ?? "",
                lastName: currentNucleusKit?.attributes.find(attribute => attribute.name === "lastName")?.value ?? "",
                dateOfBirth: new Date(dateOfBirth.substring(0, 4) + "-" + dateOfBirth.substring(4, 6) + "-" + dateOfBirth.substring(6, 8)),
                productCode: agenaPanelStatus?.productCode ?? "",
                productName: agenaPanelStatus?.productName ?? "",
                sampleCollectionDate: new Date(2000, 1, 1, 0, 0, 0, 0),
                sampleType: "Buccal Swab",
                sampleErrorNote: "",
                createdBy: authContext?.user?.getUsername()!,
                approvedAt: "",
                dateCreated: new Date()
            }

        // Save first, so a kit is only marked as received in Nucleus once LIMS holds its details.
        UpsertCompWellnessPatient(patient)
            .then(response => {
                // update patient in state
                if (!response.isSuccess) {
                    setToastError("Error when attempting to save patient details");
                    return;
                }

                CompWellnessSpecimenReceived(patient.kitNumber)
                    .then(response => {
                        if (!response?.success) {
                            setToastError("Error when attempting to update Nucleus with specimenReceived");
                            return;
                        }
                    })
                    .catch(error => {
                        var ErrorMessage = "Error when attempting to update Nucleus with specimen received.";
                        console.error(ErrorMessage.concat("\r\n").concat(getApiErrorMessage(error)));
                        setToastError(ErrorMessage);
                        setToastOpen(true);
                    })

                // double check if the patient has been stored in the LIMS database.
                GetCompWellnessPatients()
                .then(patients => {
                    var kitNumber = currentNucleusKit?.kitNumber ?? "";
                    if (patients.find(patient => patient.kitNumber === currentNucleusKit?.kitNumber) != null) 
                        setToastError("The patient details for kit number \"" + kitNumber + "\" have been stored in LIMS.")
                    else {
                        setToastError("Error inserting patient details for Nucleus kit number \"" + kitNumber + "\" into database. Try again. If this occurs again, please contact IT.");
                        alert("Error inserting patient details for Nucleus kit number \"" + kitNumber + "\" into database. Try again. If this occurs again, please contact IT.");
                    }
                })
                .catch(error => {
                    var message = "Error fetching Nucleus Patients data from database. Failed to validate that the patient details have been stored in LIMS.";
                    setToastError(message);
                    console.error(message);
                    console.error(error);
                    alert(message);
                });
            })
            .catch(error => {
                let errorMessage = "Error when attempting to save patient details. ";
                GraphqlExceptionLogging(error, errorMessage)
                var apiErrorMessage = getApiErrorMessage(error);
                if (apiErrorMessage) {
                    alert(apiErrorMessage);
                }
                setToastError(errorMessage + apiErrorMessage);
            })
        setToastOpen(true);
    }

    const toggleModal = () => {
        setModalOpen(modalOpen => !modalOpen);
    }
    
    return (
        <>
        <ContentWrapper>
            <Snackbar open={toastOpen} autoHideDuration={6000} onClose={handleToastClose}>
                <MuiAlert onClose={handleToastClose} variant="filled" severity={toastError === "" ? "success" : "error"} sx={{ width: '100%' }}>
                    {toastError === "" ? "Success!" : toastError}
                </MuiAlert>
            </Snackbar>
            <SectionWrapper>
                <Typography gutterBottom variant="h4" component="div">
                    Specimen Reception
                </Typography>
            </SectionWrapper>
            <Divider variant="middle" />
            <Box sx={{ minWidth: 120 }}>
                    <SectionWrapper>
                        <Typography gutterBottom variant="h6" component="span">
                        Scan Barcode:  {" "}
                        </Typography>
                        <TextField 
                            id="standard-basic" 
                            label="" 
                            variant="standard" 
                            inputRef={kitNumberRef}
                            onKeyUp={handleScanBarcode}
                        />
                    </SectionWrapper>
            </Box>
            <Divider variant="middle" />
            <Typography gutterBottom variant="h6" component="div">
                Patient details
            </Typography>
            <Box sx={{ minWidth: 120 }}>
                    <SectionWrapper>
                        <Typography gutterBottom variant="h6" component="span">
                        kit number:  {currentNucleusKit?.kitNumber ?? ""}
                        </Typography>
                    </SectionWrapper>
                    <SectionWrapper>
                        <Typography gutterBottom variant="h6" component="span">
                        first name:
                        </Typography>
                        <TextField 
                            id="first-name" 
                            label="" 
                            variant="standard"
                            value={currentNucleusKit?.attributes.find(attribute => attribute.name === "firstName")?.value ?? ""}
                            onChange={handlePatientFirstNameChange}
                        />
                    </SectionWrapper>
                    <SectionWrapper>
                        <Typography gutterBottom variant="h6" component="span">
                        last name:  {" "}
                        </Typography>
                        <TextField 
                            id="last-name" 
                            label="" 
                            variant="standard" 
                            value={currentNucleusKit?.attributes.find(attribute => attribute.name === "lastName")?.value ?? ""}
                            onChange={handlePatientLastNameChange}
                        />
                    </SectionWrapper>
                    <SectionWrapper>
                        <Typography gutterBottom variant="h6" component="span">
                        date of birth:  {" "}
                        </Typography>
                        <TextField 
                            id="dateofbirth" 
                            label="" 
                            variant="standard" 
                            value={currentNucleusKit?.attributes.find(attribute => attribute.name === "dateOfBirth")?.value ?? ""}
                            onChange={handlePatientDateOfBirthChange}
                        />
                    </SectionWrapper>
                    <SectionWrapper>
                        <Typography gutterBottom variant="h6" component="span">
                        gender:  {" "}
                        </Typography>
                        <TextField 
                            id="gender" 
                            label="" 
                            variant="standard" 
                            value={currentNucleusKit?.gender ?? ""}
                            onChange={handlePatientGenderChange}
                        />
                    </SectionWrapper>
                    <SectionWrapper>
                        <Typography gutterBottom variant="h6" component="span">
                        GRC number:  {currentNucleusGrc?.grcNumber ?? ""}
                        </Typography>
                    </SectionWrapper>
            </Box>
            <Divider variant="middle" />
            <Box sx={{ minWidth: 120 }}>
                    <SectionWrapper>
                        <Typography gutterBottom variant="h6" component="span">
                        product:  {findAgenaPanelStatus(currentNucleusKit)?.productName ?? ""}
                        </Typography>
                    </SectionWrapper>
            </Box>
            <Divider variant="middle" />
            <Box sx={{ minWidth: 120 }}>
                <Button color="success" variant="contained" onClick={handleSavePatientDetails} disabled={!isReadyToSave}>Save</Button>
            </Box>
        </ContentWrapper>
        <Modal
            open={modalOpen}
            onClose={() => toggleModal()}
        >
            <Box sx={ModalStyle}>
                <WarningIcon />
                <Typography variant="h6" component="h2">
                    {modalTextElement}
                </Typography>
                <Button color="success" variant="contained" onClick={toggleModal}>OK</Button>
            </Box>
        </Modal>
        </>
    )
};

export default ComprehensiveWellnessSpecimenReceptionPage;